import React, { useEffect, useRef } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { CircularProgress, ThemeProvider } from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';

import { Product, getTheme } from '@/utils/productHelpers';
import { getSupabaseClient } from '@/utils/supabaseClient';
import { activateSparkConfirm, requestAuthCode } from '@/utils/sparkActivationApi';
import { REDIRECT_TO_SESSION_KEY } from '@/utils/constants';
import { isSafeRedirectUri } from '@/utils/urlHelpers';
import { ME_QUERY, QUERY_DISTINCT_ID_WITH_EMAIL } from '@/apollo/queries';
import { useEnsureActivated } from '@/hooks/useEnsureActivated';
import { logError } from '@/utils/logError';
import { linkPostHogIdentity } from '@/utils/posthog';
import { getEntitlementStatus, toEntitlementApp } from '@/utils/entitlements';

const SparkEmailCallback: React.FC = () => {
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const { search } = useLocation();
  const posthog = usePostHog();
  const [fetchMe] = useLazyQuery<{ me: { uid: string } }>(ME_QUERY, { fetchPolicy: 'no-cache' });
  const [getDistinctId] = useLazyQuery(QUERY_DISTINCT_ID_WITH_EMAIL, { fetchPolicy: 'network-only' });
  const ensureActivated = useEnsureActivated(product);
  const callbackStarted = useRef(false);

  useEffect(() => {
    const handleLoginFlow = async () => {
      const {
        data: { session: refreshedSession },
        error: refreshError,
      } = await getSupabaseClient().auth.refreshSession();

      if (refreshError || !refreshedSession?.access_token || !refreshedSession?.refresh_token) {
        logError('[SparkEmailCallback]', 'refreshSession failed', refreshError);
        history.replace('/spark/login');
        return;
      }

      const redirectTo = sessionStorage.getItem(REDIRECT_TO_SESSION_KEY);

      if (!redirectTo || !isSafeRedirectUri(redirectTo)) {
        history.replace(`/activate/${product}/download-app`);
        return;
      }

      let exchangeToken: string;
      try {
        ({ exchangeToken } = await requestAuthCode(
          refreshedSession.access_token,
          refreshedSession.refresh_token,
        ));
      } catch (err) {
        logError('[SparkEmailCallback]', 'requestAuthCode failed', err);
        history.replace('/spark/login');
        return;
      }

      const { data: meData } = await fetchMe({
        context: { headers: { Authorization: `Bearer ${refreshedSession.access_token}` } },
      });
      const limbixUserUid = meData?.me?.uid;

      const deepLinkUrl = new URL(redirectTo);
      deepLinkUrl.searchParams.set('exchange_token', exchangeToken);
      if (limbixUserUid) {
        deepLinkUrl.searchParams.set('limbix_user_uid', limbixUserUid);
      }

      window.location.href = deepLinkUrl.toString();
    };

    const handleCallback = async () => {
      try {
        const redirectToParam = new URLSearchParams(search).get('redirectTo');
        if (redirectToParam && isSafeRedirectUri(redirectToParam)) {
          sessionStorage.setItem(REDIRECT_TO_SESSION_KEY, redirectToParam);
        }

        await new Promise((resolve) => { setTimeout(resolve, 1500); });

        const { data: { session } } = await getSupabaseClient().auth.getSession();

        if (!session) {
          history.replace(`/activate/${product}/access-code`, { error: 'email_verification_failed' });
          return;
        }

        const supabaseUserId = session.user?.id ?? '';
        const patientUid = session.user?.user_metadata?.limbixUserId || '';
        // An unrecognised product segment reads as "cannot tell", same safe default as
        // an unreadable token — never as a returning user.
        const entitlementApp = toEntitlementApp(product);
        const entitlementStatus = entitlementApp
          ? getEntitlementStatus(session.access_token, entitlementApp)
          : undefined;
        const rawAccessCode = session.user?.user_metadata?.accessCode;
        const accessCode = typeof rawAccessCode === 'string' && rawAccessCode.trim()
          ? rawAccessCode.trim()
          : null;
        const isDependent = session.user?.user_metadata?.isDependent === true;
        // Whether to skip activateSparkConfirm depends only on actual entitlement status —
        // NOT on isDependent. A dependent's FIRST magic link still needs confirm to run
        // (it's what flips entitlement pending → active); skipping it here left every
        // dependent stuck with a pending entitlement and no way to complete session
        // handoff (SD-2335). Once a dependent's entitlement is active, later magic-link
        // clicks correctly skip confirm same as any other returning user.
        const isReturningUser = !accessCode && entitlementStatus === 'active';
        const email = session.user?.email;

        if (isReturningUser) {
          // The entitlement flips to active before Cortex is told, so check before handing off.
          // A failed activation is logged and retried on their next login, never a locked door.
          const outcome = await ensureActivated(session.access_token);

          // Await with 2s cap — handleLoginFlow's window.location.href kills the page. Above
          // the divert so the users this fix targets are still identified in PostHog.
          if (email) {
            await Promise.race([
              linkPostHogIdentity(getDistinctId, posthog, email, '[SparkEmailCallback]'),
              new Promise<void>((resolve) => { setTimeout(resolve, 2000); }),
            ]);
          }

          if (outcome.action === 'coverage') {
            history.replace(`/activate/${product}/coverage-check/${outcome.orgSlug}`, {
              patientUid,
              flow: 'email',
              accessToken: session.access_token,
            });
            return;
          }

          await handleLoginFlow();
          return;
        }

        const orgSlug = new URLSearchParams(search).get('org_slug');

        try {
          await activateSparkConfirm({ supabaseUserId, accessToken: session.access_token });
        } catch (err) {
          logError('[SparkEmailCallback]', 'activation failed', err, { supabaseUserId });
          history.replace(`/activate/${product}/access-code`, { error: 'email_verification_failed' });
          return;
        }

        // Dependents have their name/DOB and coverage check completed by their caregiver
        // during enrollment, before the dependent ever receives a magic link — so once
        // confirm has activated their entitlement, skip straight to the logged-in handoff
        // flow instead of re-showing coverage-check/user-info screens. (SD-2335)
        if (isDependent) {
          // The coverage-check screen skipped below is the only place Spark sets enrolled_time,
          // so activate here — and block on failure, as the coverage-check screen does.
          const dependentOutcome = await ensureActivated(session.access_token, orgSlug);

          // Await with 2s cap — handleLoginFlow's window.location.href kills the page. Above
          // the diverts so the users this fix targets are still identified in PostHog.
          if (email) {
            await Promise.race([
              linkPostHogIdentity(getDistinctId, posthog, email, '[SparkEmailCallback]'),
              new Promise<void>((resolve) => { setTimeout(resolve, 2000); }),
            ]);
          }

          if (dependentOutcome.action === 'error') {
            history.replace(`/activate/${product}/access-code`, { error: 'activation_failed' });
            return;
          }
          if (dependentOutcome.action === 'coverage') {
            history.replace(`/activate/${product}/coverage-check/${dependentOutcome.orgSlug}`, {
              patientUid,
              flow: 'email',
              accessToken: session.access_token,
            });
            return;
          }

          await handleLoginFlow();
          return;
        }

        // Fire-and-forget: history.replace keeps the Apollo client alive.
        if (email) {
          linkPostHogIdentity(getDistinctId, posthog, email, '[SparkEmailCallback]');
        }

        if (orgSlug && !accessCode) {
          history.replace(`/activate/${product}/coverage-check/${orgSlug}`, {
            patientUid,
            flow: 'email',
            accessToken: session.access_token,
          });
        } else {
          history.replace(`/activate/${product}/user-info/confirmation`, {
            patientUid,
            flow: 'email',
            accessCode,
          });
        }
      } catch (err) {
        logError('[SparkEmailCallback]', 'Unexpected error', err);
        history.replace(`/activate/${product}/access-code`, { error: 'email_verification_failed' });
      }
    };

    // One-shot flow: a re-run would fire confirm and activation a second time.
    if (callbackStarted.current) { return; }
    callbackStarted.current = true;
    handleCallback();
  }, [history, product, search, fetchMe, getDistinctId, posthog, ensureActivated]);

  return (
    <ThemeProvider theme={getTheme(product)}>
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      }}
      >
        <CircularProgress color="primary" />
      </div>
    </ThemeProvider>
  );
};

export default SparkEmailCallback;
